import React from 'react';
import { Spinner } from './Spinner';
import { EmptyState } from './EmptyState';
import './TableComponent.scss';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from './ButtonComponent';
import { Title } from './developers/page-components/Title';
import clsx from 'clsx';
import { IFees } from '../store/merchnts/merchant-interfaces';

export interface IColumnProps {
	key: string;
	title?: string;
	additionalColumnClassName?: string;
	isAdditionalHeaderRow?: boolean;
}

interface Props {
	isDataLoading?: boolean;
	title?: string;
	buttonName?: string;
	column: IColumnProps[];
	tableData: any[] | IFees[];
	getRowComponent: any;
	onButtonClick?: () => void;
	additionalColumnStart?: JSX.Element;
	additionalColumnEnd?: JSX.Element;
	additionalButtonClass?: string;
	additionalTitleClass?: string;
	headerElement?: JSX.Element;
	tableMinHeight?: string;
	tableLoadingHeight?: string;
	additionalTableHeaderRow?: any[];
}

export const TableComponent = (props: Props): React.ReactElement => {
	const {
		isDataLoading,
		column,
		tableData,
		getRowComponent,
		title,
		buttonName,
		onButtonClick,
		additionalColumnStart,
		additionalColumnEnd,
		additionalButtonClass,
		headerElement,
		tableMinHeight='table-min-height',
		tableLoadingHeight,
		additionalTitleClass,
		additionalTableHeaderRow
	} = props;
	const { t } = useTranslation();

// @ts-ignore
	const tableBody = Boolean(tableData?.length) && tableData?.map((item, index) => getRowComponent(item, index));

	const tableEmptyState = !tableData?.length && <EmptyState title="No data was found!"/>;


	const getTableHeader = (): JSX.Element[] => (
		column?.map(col => (
			<th
				key={col.key}
				className={col?.additionalColumnClassName}
			>
				<div className="d-flex align-items-center fs-8">
					{t(col?.title)}
				</div>
			</th>
		))
	);

	const getAdditionalTableHeader = (): JSX.Element[] => (
		additionalTableHeaderRow?.map(col => (
			<th key={col.key}>
				<div className="d-flex align-items-center fs-8">
					{col.isTitle ? t(col?.title) : ''}
				</div>
			</th>
		))
	);

	const getPageLoader = (): JSX.Element => (
		<div className={clsx('card', isDataLoading && tableLoadingHeight ? tableLoadingHeight : 'table-min-height')}>
			<div className="card-body">
				<Spinner pageLoader/>
			</div>
		</div>
	);


	return (
		<>
			{
				title && (
					<div className="card-header d-flex align-items-center justify-content-between">
						<Title title={t(title)} additionalClass={clsx('fs-2', additionalTitleClass)}/>
						{
							buttonName && (
								<ButtonComponent
									buttonName={buttonName}
									onButtonClick={onButtonClick}
									additionalClass={additionalButtonClass}
								/>
							)
						}
						{headerElement && headerElement}
					</div>
				)
			}
			{
				// isDataLoading || !tableData?.length
				isDataLoading
					? getPageLoader()
					: (
						<div className="card-body py-3">
							<div className={clsx('table-responsive position-relative', tableMinHeight)}>
								<table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
									<thead>
									<tr className="fw-bolder text-muted">
										{additionalColumnStart}

										{getTableHeader()}

										{additionalColumnEnd}
									</tr>
									{additionalTableHeaderRow && (
										<tr className="fw-bolder text-muted">
											{getAdditionalTableHeader()}
										</tr>
									)
									}
									</thead>
									<tbody>
									{ tableBody }
									</tbody>
								</table>
								{tableEmptyState}
							</div>
						</div>
					)
			}

		</>
	);
};
